import React, { useEffect } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { applyTypography, useAuth, useLang, useT } from '@repo/common';

import PolicyPage from './PolicyPage';
import PolicyToolbar from './PolicyToolbar';
import { POLICIES, formatPolicyDate, policyDateLabel, versionPath } from './policyRegistry';
import { availableLangs, usePolicyDocument, usePolicyVersions } from './usePolicyVersions';

/** 기업회원으로 볼 역할. FooterV2의 판정 기준과 같다 */
const BUSINESS_ROLES = new Set([
  'E',
  'ROLE_E',
  'EMPLOYER',
  'ROLE_EMPLOYER',
  'EMPLOYEE',
  'ROLE_EMPLOYEE',
]);

/** 개인용 정책 → 기업회원이 봐야 할 정책 */
const BUSINESS_COUNTERPART = {
  terms: 'business-terms',
  privacy: 'business-privacy',
};

const useUiDate = () => {
  const { lang } = useLang();
  return (iso) => formatPolicyDate(iso, lang === 'kr' ? 'ko' : 'en');
};

const UpcomingNotice = ({ policyKey, upcoming, search }) => {
  const t = useT();
  const uiDate = useUiDate();
  const date = uiDate(upcoming.effectiveDate);
  return (
    <div
      className={`${applyTypography('b14')} w-full rounded-[12px] border border-functional-warning03 bg-functional-warning01 px-[16px] py-[12px] text-text-primary`}
    >
      {t(`This policy will be updated on ${date}.`, `${date}부터 개정된 정책이 시행됩니다.`)}{' '}
      <Link to={`${versionPath(policyKey, upcoming.effectiveDate)}${search}`} className='underline'>
        {t('Preview the new version', '개정 내용 미리 보기')}
      </Link>
    </div>
  );
};

const VersionFooter = ({ policyKey, upcoming, effectiveDate, search }) => {
  const t = useT();
  const date = useUiDate()(effectiveDate);
  return (
    <p
      className={`${applyTypography('b14')} w-full border-t border-grayscale-gray02 pt-8 text-text-placeholder`}
    >
      {upcoming
        ? t(`This version takes effect on ${date}.`, `이 문서는 ${date}부터 시행될 예정입니다.`)
        : t('This is a previous version of this policy.', '이 문서는 이전 버전의 정책입니다.')}{' '}
      <Link to={`${POLICIES[policyKey].path}${search}`} className='underline'>
        {t('View the current version', '현재 버전 보기')}
      </Link>
    </p>
  );
};

/**
 * 정책 문서 페이지.
 *
 * <p>`/terms` 는 시행 중인 최신 버전, `/terms/2026-06-11` 은 해당 시행일 버전을 보여준다.
 * 없는 버전으로 들어오면 최신 버전으로 돌려보낸다.
 *
 * <p>기업회원으로 로그인한 상태에서 `/terms`·`/privacy`에 들어오면
 * 기업회원용 국문 문서로 보낸다. 비로그인·개인회원은 그대로 영문 개인회원 문서를 본다.
 */
const PolicyDocument = ({ policyKey }) => {
  const { version } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: isAuthLoading } = useAuth();
  const { lang: siteLang } = useLang();

  const isBusiness = BUSINESS_ROLES.has(String(user?.role ?? '').toUpperCase());
  const businessKey = BUSINESS_COUNTERPART[policyKey];

  const { loaded, all, upcoming, current } = usePolicyVersions(policyKey);
  const selected = version
    ? all.find((item) => item.effectiveDate === version) ?? null
    : current;

  const langParam = params.get('lang');
  const requested =
    langParam === 'ko' || langParam === 'en' ? langParam : siteLang === 'kr' ? 'ko' : 'en';
  const available = availableLangs(selected);
  const lang = available.includes(requested) ? requested : available[0] ?? requested;

  const { loaded: documentLoaded, document } = usePolicyDocument(policyKey, selected, lang);
  const isUpcoming = !!selected && upcoming.includes(selected);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [policyKey, version]);

  // 역할 판정 전에 개인용 문서를 잠깐 보여주면 깜빡이므로 기다린다
  if (isAuthLoading && businessKey) return null;

  // 기업회원은 기업회원용 문서로. 특정 버전을 지정해 들어온 경우는 그 의도를 존중한다
  if (isBusiness && businessKey && !version) {
    return <Navigate to={POLICIES[businessKey].path} replace />;
  }

  if (!loaded) return null;

  // 존재하지 않는 버전 → 최신으로 (오래된 링크가 404로 죽지 않게)
  if (version && !selected) {
    return <Navigate to={POLICIES[policyKey].path} replace />;
  }

  if (!documentLoaded) return null;

  const fallbackVersion = [...all].reverse().find((item) => item.source === 'bundled');
  const shown =
    document ??
    fallbackVersion?.languages[lang]?.document ??
    fallbackVersion?.languages[availableLangs(fallbackVersion)[0]]?.document ??
    null;
  if (!shown) return null;

  const search = langParam ? `?lang=${langParam}` : '';
  const hasBilingual = all.some((item) => availableLangs(item).length > 1);
  const isCurrent = selected === current;
  const nextUpcoming = isCurrent ? upcoming[0] : null;

  const changeVersion = (effectiveDate) => {
    const path =
      effectiveDate === current?.effectiveDate
        ? POLICIES[policyKey].path
        : versionPath(policyKey, effectiveDate);
    navigate(`${path}${search}`);
  };

  const changeLang = (next) => {
    const nextParams = new URLSearchParams(params);
    nextParams.set('lang', next);
    setParams(nextParams, { replace: true });
  };

  return (
    <PolicyPage
      title={shown.title}
      lastUpdated={shown.lastUpdated}
      lastUpdatedLabel={isUpcoming ? policyDateLabel(lang, true) : shown.lastUpdatedLabel}
      toolbar={
        selected && (
          <PolicyToolbar
            versions={all}
            selected={selected}
            current={current}
            lang={lang}
            hasBilingual={hasBilingual}
            onVersionChange={changeVersion}
            onLangChange={changeLang}
          />
        )
      }
      notice={
        nextUpcoming && (
          <UpcomingNotice policyKey={policyKey} upcoming={nextUpcoming} search={search} />
        )
      }
      sections={shown.sections}
      footer={
        selected &&
        !isCurrent && (
          <VersionFooter
            policyKey={policyKey}
            upcoming={isUpcoming}
            effectiveDate={selected.effectiveDate}
            search={search}
          />
        )
      }
    />
  );
};

export default PolicyDocument;

/** 라우트에서 바로 쓰기 위한 얇은 래퍼들 */
export const TermsPage = () => <PolicyDocument policyKey='terms' />;
export const PrivacyPage = () => <PolicyDocument policyKey='privacy' />;
export const BusinessTermsPage = () => (
  <PolicyDocument policyKey='business-terms' />
);
export const BusinessPrivacyPage = () => (
  <PolicyDocument policyKey='business-privacy' />
);
export const RefundPolicyPage = () => <PolicyDocument policyKey='refund' />;

export { versionPath };
